import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { Col, Input, Label, Row } from "reactstrap";
import AppModal from "../../../../Components/Common/AppModal";
import CommunicationSubscribersField, {
  useCommunicationSubscribers,
  usesSubscribers,
} from "./CommunicationSubscribers";

import {
  createCommunicationChannel,
  updateCommunicationChannel,
  createCommunicationChannelSuccess,
  updateCommunicationChannelSuccess,
  getCommunicationChannelList,
} from "../../../../store/communication/action";

// Channel meta: which template FK column + which store list feeds the dropdown.
const CHANNEL_META = {
  EMAIL: { label: "Email", icon: "ri-mail-line", templateField: "EmailTemplate_Id" },
  SMS: { label: "SMS", icon: "ri-message-2-line", templateField: "SmsTemplate_Id" },
  NOTIFICATION: {
    label: "In-app Notification",
    icon: "ri-notification-3-line",
    templateField: "NotificationTemplate_Id",
  },
};

const flagOn = (v) => v === "1" || v === 1 || v === true;

export default function CommunicationChannelModal({
  modal,
  toggle,
  communication,
}) {
  const dispatch = useDispatch();
  const communicationId = communication?.Id;

  const {
    channelListAPI,
    placeholderListAPI,
    emailTemplates,
    smsTemplates,
    notificationTemplates,
    createResponse,
    updateResponse,
    createLoading,
    updateLoading,
  } = useSelector((state) => ({
    channelListAPI:
      state.CommunicationReducer?.communicationChannelList
        ?.communicationchannel_list || [],
    placeholderListAPI:
      state.CommunicationReducer?.communicationPlaceholderList
        ?.communicationplaceholder_list || [],
    notificationTemplates:
      state.CommunicationReducer?.notificationTemplateList
        ?.notificationtemplate_list || [],
    createResponse: state.CommunicationReducer?.createCommunicationChannel,
    updateResponse: state.CommunicationReducer?.updateCommunicationChannel,
    createLoading: state.CommunicationReducer?.createCommunicationChannelLoading,
    updateLoading: state.CommunicationReducer?.updateCommunicationChannelLoading,
    smsTemplates: state.SmsReducer?.smsTemplateList?.smstemplate_list || [],
    emailTemplates:
      state.EmailInbound?.emailTemplateList?.emailtemplate_list || [],
  }));

  // Change #100528 - staff subscribers, saved alongside the channels.
  const showSubscribers = usesSubscribers(communication);
  const subscribers = useCommunicationSubscribers(
    communicationId,
    showSubscribers
  );
  const [savingSubscribers, setSavingSubscribers] = useState(false);

  const saving = createLoading || updateLoading || savingSubscribers;
  const [pendingSave, setPendingSave] = useState(false);

  // Per-channel form state keyed by channel name.
  const [channels, setChannels] = useState({});

  // Template option list for a channel.
  const templateOptions = (channel) => {
    if (channel === "EMAIL")
      return emailTemplates.map((t) => ({
        id: t.Id,
        label: t.Title || t.Subject || `Template #${t.Id}`,
      }));
    if (channel === "SMS")
      return smsTemplates.map((t) => ({
        id: t.Id,
        label: t.Title || `Template #${t.Id}`,
      }));
    return notificationTemplates.map((t) => ({
      id: t.Id,
      label: t.Title || `Template #${t.Id}`,
    }));
  };

  // Hydrate the form from existing channel rows whenever the modal opens.
  useEffect(() => {
    if (!communicationId) return;
    const next = {};
    Object.keys(CHANNEL_META).forEach((channel) => {
      const row = channelListAPI.find(
        (ch) =>
          String(ch.Communication_Id) === String(communicationId) &&
          ch.Channel === channel
      );
      next[channel] = {
        Id: row?.Id || null,
        FlagCompanyEnabled: flagOn(row?.FlagCompanyEnabled),
        FlagCompanyEnforced: flagOn(row?.FlagCompanyEnforced),
        FlagMandatory: flagOn(row?.FlagMandatory),
        templateId:
          row?.[CHANNEL_META[channel].templateField] != null
            ? String(row[CHANNEL_META[channel].templateField])
            : "",
      };
    });
    setChannels(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communicationId, channelListAPI]);

  const placeholders = useMemo(
    () =>
      placeholderListAPI.filter(
        (p) => String(p.Communication_Id) === String(communicationId)
      ),
    [placeholderListAPI, communicationId]
  );

  const setChannelField = (channel, field, value) =>
    setChannels((prev) => ({
      ...prev,
      [channel]: { ...prev[channel], [field]: value },
    }));

  const handleSave = async () => {
    const toCreate = [];
    const toUpdate = [];

    Object.keys(CHANNEL_META).forEach((channel) => {
      const cfg = channels[channel];
      if (!cfg) return;

      // Persist a row if it already exists, is enabled, or has a template chosen.
      const shouldPersist =
        cfg.Id || cfg.FlagCompanyEnabled || cfg.templateId;
      if (!shouldPersist) return;

      const row = {
        Communication_Id: communicationId,
        Channel: channel,
        [CHANNEL_META[channel].templateField]: cfg.templateId || null,
        FlagCompanyEnabled: cfg.FlagCompanyEnabled ? "1" : "0",
        FlagCompanyEnforced: cfg.FlagCompanyEnforced ? "1" : "0",
        FlagMandatory: cfg.FlagMandatory ? "1" : "0",
      };

      if (cfg.Id) {
        toUpdate.push({ Id: cfg.Id, ...row });
      } else {
        toCreate.push(row);
      }
    });

    const subscribersChanged = showSubscribers && subscribers.dirty;

    if (isEmpty(toCreate) && isEmpty(toUpdate) && !subscribersChanged) {
      toast.info("Nothing to save.");
      return;
    }

    if (subscribersChanged) {
      setSavingSubscribers(true);
      const ok = await subscribers.save();
      setSavingSubscribers(false);
      if (!ok) {
        toast.error("Failed to save staff subscribers");
        return;
      }
      if (isEmpty(toCreate) && isEmpty(toUpdate)) {
        toast.success("Staff subscribers updated");
        toggle();
        return;
      }
    }

    setPendingSave(true);
    if (!isEmpty(toUpdate)) dispatch(updateCommunicationChannel(toUpdate));
    if (!isEmpty(toCreate)) dispatch(createCommunicationChannel(toCreate));
  };

  // React to save responses (only when a save is in flight).
  useEffect(() => {
    if (!pendingSave) return;
    const res = updateResponse;
    if (isEmpty(res)) return;
    if (res?.status === 200) {
      toast.success(res?.message || "Channels updated");
      dispatch(getCommunicationChannelList());
      setPendingSave(false);
      dispatch(updateCommunicationChannelSuccess({}));
      toggle();
    } else {
      toast.error(res?.message || "Failed to update channels");
      setPendingSave(false);
      dispatch(updateCommunicationChannelSuccess({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResponse]);

  useEffect(() => {
    if (!pendingSave) return;
    const res = createResponse;
    if (isEmpty(res)) return;
    if (res?.status === 200) {
      toast.success(res?.message || "Channels created");
      dispatch(getCommunicationChannelList());
      setPendingSave(false);
      dispatch(createCommunicationChannelSuccess({}));
      toggle();
    } else {
      toast.error(res?.message || "Failed to create channels");
      setPendingSave(false);
      dispatch(createCommunicationChannelSuccess({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createResponse]);

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={`Configure Channels — ${
        communication?.Title || communication?.Code || ""
      }`}
      headerAccent="indigo"
      size="md"
      loading={saving}
      onSubmit={handleSave}
      submitLabel="Save"
      submitColor="primary"
    >
        {communication?.Description && (
          <p className="text-muted">{communication.Description}</p>
        )}

        {/* Placeholders available to the templates */}
        <div className="mb-3">
          <Label className="mb-1">Available placeholders</Label>
          <div>
            {placeholders.length ? (
              placeholders.map((p) => (
                <span
                  key={p.Id}
                  className="badge bg-light text-dark border me-1 mb-1"
                  title={p.Description || p.Label || ""}
                >
                  {`{{${p.Token}}}`}
                </span>
              ))
            ) : (
              <small className="text-muted">
                No placeholders defined for this communication.
              </small>
            )}
          </div>
        </div>

        <hr />

        {showSubscribers && (
          <CommunicationSubscribersField
            loading={subscribers.loading}
            selectedIds={subscribers.selectedIds}
            setSelectedIds={subscribers.setSelectedIds}
          />
        )}

        {Object.keys(CHANNEL_META).map((channel) => {
          const meta = CHANNEL_META[channel];
          const cfg = channels[channel] || {};
          return (
            <div key={channel} className="border rounded p-3 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">
                  <i className={`${meta.icon} me-2`}></i>
                  {meta.label}
                </h6>
                <div className="form-check form-switch mb-0">
                  <Input
                    type="checkbox"
                    role="switch"
                    id={`enabled-${channel}`}
                    checked={!!cfg.FlagCompanyEnabled}
                    onChange={(e) =>
                      setChannelField(
                        channel,
                        "FlagCompanyEnabled",
                        e.target.checked
                      )
                    }
                  />
                  <Label
                    className="form-check-label"
                    for={`enabled-${channel}`}
                  >
                    Enabled
                  </Label>
                </div>
              </div>

              <Row className="g-3">
                <Col md={12}>
                  <Label className="form-label">Template</Label>
                  <Input
                    type="select"
                    value={cfg.templateId || ""}
                    onChange={(e) =>
                      setChannelField(channel, "templateId", e.target.value)
                    }
                  >
                    <option value="">— No template —</option>
                    {templateOptions(channel).map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md={6}>
                  <div className="form-check form-switch">
                    <Input
                      type="checkbox"
                      role="switch"
                      id={`enforced-${channel}`}
                      checked={!!cfg.FlagCompanyEnforced}
                      onChange={(e) =>
                        setChannelField(
                          channel,
                          "FlagCompanyEnforced",
                          e.target.checked
                        )
                      }
                    />
                    <Label
                      className="form-check-label"
                      for={`enforced-${channel}`}
                    >
                      Company enforced
                      <small className="d-block text-muted">
                        Staff can&apos;t override this setting.
                      </small>
                    </Label>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="form-check form-switch">
                    <Input
                      type="checkbox"
                      role="switch"
                      id={`mandatory-${channel}`}
                      checked={!!cfg.FlagMandatory}
                      onChange={(e) =>
                        setChannelField(
                          channel,
                          "FlagMandatory",
                          e.target.checked
                        )
                      }
                    />
                    <Label
                      className="form-check-label"
                      for={`mandatory-${channel}`}
                    >
                      Mandatory
                      <small className="d-block text-muted">
                        Recipients can never opt out (transactional/legal).
                      </small>
                    </Label>
                  </div>
                </Col>
              </Row>
            </div>
          );
        })}
    </AppModal>
  );
}
